import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { renderActiveStatusChip } from 'utils/statusChipUtils';

import Button from 'components/Button/Button';
import { TableCellEllipsis } from 'components/Orders/Orders/Components/TableCellEllipsis';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger
} from 'components/ui/dropdown-menu';

import { Coupon } from '../../../common/contracts';
import { DATE_TIME_FORMAT } from '../../../constants/constants';

interface CouponsTableColumnsParams {
  navigate: (path: string) => void;
  toggleActivateCoupon: (couponId: string, active: boolean) => void;
  isDisabledDueToPermissions: boolean;
  isCouponExpired: (expiredBy: string) => boolean;
}

export const COUPONS_TABLE_COLUMNS = ({
  navigate,
  toggleActivateCoupon,
  isDisabledDueToPermissions,
  isCouponExpired
}: CouponsTableColumnsParams): ColumnDef<Coupon>[] => [
  {
    accessorKey: 'name',
    header: 'Coupon Name',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    ),
    enableSorting: true
  },
  {
    accessorKey: 'promoCodeNames',
    header: 'Promo Code',
    cell: ({ getValue }) => {
      const codes = getValue() as string[] | undefined;
      return <TableCellEllipsis>{codes?.join(', ') || '-'}</TableCellEllipsis>;
    },
    enableSorting: false
  },
  {
    accessorKey: 'discountPercentage',
    header: 'Discount',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as number}%</TableCellEllipsis>
    ),
    enableSorting: true
  },
  {
    accessorKey: 'redemptions',
    header: 'Redemptions',
    cell: ({ row }) => (
      <TableCellEllipsis>
        {row.original.currentRedemptions}/{row.original.maxRedemptions}
      </TableCellEllipsis>
    ),
    enableSorting: false
  },
  {
    accessorKey: 'startsAt',
    header: 'Starts At',
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return (
        <TableCellEllipsis>
          {value ? dayjs(value).utc().format(DATE_TIME_FORMAT) : 'N/A'}
        </TableCellEllipsis>
      );
    },
    enableSorting: true
  },
  {
    accessorKey: 'expiredBy',
    header: 'Expires On',
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return (
        <TableCellEllipsis>
          {value ? dayjs(value).utc().format(DATE_TIME_FORMAT) : 'Never'}
        </TableCellEllipsis>
      );
    },
    enableSorting: true
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return (
        <TableCellEllipsis>
          {value ? dayjs(value).utc().format(DATE_TIME_FORMAT) : 'N/A'}
        </TableCellEllipsis>
      );
    },
    enableSorting: true
  },
  {
    accessorKey: 'active',
    header: 'Status',
    cell: ({ getValue, row }) => {
      const value = getValue() as boolean;
      const isExpired = isCouponExpired(row.original.expiredBy || '');
      const isActive = value && !isExpired;

      return renderActiveStatusChip(isActive);
    },
    enableSorting: true
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const isExpired = isCouponExpired(row.original.expiredBy || '');

      return (
        <div className="flex justify-end w-full">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent align="end" className="bg-base-popover">
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-[#F4F4F5]"
                  onClick={() => navigate(`./${row.original.id}`)}
                  disabled={isDisabledDueToPermissions}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-[#F4F4F5]"
                  onClick={() =>
                    toggleActivateCoupon(row.original.id, !row.original.active)
                  }
                  disabled={isDisabledDueToPermissions || isExpired}
                >
                  {row.original.active ? 'Deactivate' : 'Activate'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        </div>
      );
    },
    enableSorting: false
  }
];
